// The top bar depends on the role (SPEC §9.4): navigation, read-only badge, bell, language, theme, user menu.

import type { Role } from "../api/types";
import { t, type TranslationKey } from "../i18n/i18n";
import { getLanguage, getTheme, setLanguage, setTheme, type Language, type ThemeChoice } from "../preferences";
import { currentPath, redrawCurrentPage } from "../router";
import { requireUser } from "../session";
import { buildBell } from "./bell";
import { appendChildren, createBadge, createButton, createElement, createLink } from "./dom";
import { createSelect } from "./forms";

interface NavItem {
  route: string;
  labelKey: TranslationKey;
  roles: Role[];
}

const navItems: NavItem[] = [
  { route: "/day", labelKey: "nav.dayView", roles: ["Admin", "Viewer"] },
  { route: "/groups", labelKey: "nav.groups", roles: ["Admin", "Viewer"] },
  { route: "/teachers", labelKey: "nav.teachers", roles: ["Admin", "Viewer"] },
  { route: "/changes", labelKey: "nav.changes", roles: ["Admin", "Viewer"] },
  { route: "/admin/centers", labelKey: "nav.centers", roles: ["Admin", "Viewer"] },
  { route: "/admin/courses", labelKey: "nav.courses", roles: ["Admin", "Viewer"] },
  { route: "/admin/users", labelKey: "nav.users", roles: ["Admin"] },
  { route: "/my-schedule", labelKey: "nav.mySchedule", roles: ["Teacher"] },
];

let topBarContainer: HTMLElement | null = null;
let signOutHandler: () => Promise<void> = (): Promise<void> => Promise.resolve();

/** Where the bar lives and what "Sign out" does. */
export function setUpTopBar(container: HTMLElement, onSignOut: () => Promise<void>): void {
  topBarContainer = container;
  signOutHandler = onSignOut;
}

/** Draws the bar for the signed-in user, or nothing when signed out. */
export function renderTopBar(signedIn: boolean): void {
  if (topBarContainer === null) {
    return;
  }

  if (signedIn === false) {
    topBarContainer.replaceChildren();
    return;
  }

  const bar: HTMLElement = createElement("header", "topbar");
  appendChildren(bar, createLink("/", t("app.productName"), "topbar-brand"), buildNav(), buildTools());
  topBarContainer.replaceChildren(bar);
}

/** Marks the link of the current section. */
export function highlightCurrentNav(): void {
  const path: string = currentPath();
  for (const link of document.querySelectorAll<HTMLAnchorElement>(".topbar-nav a")) {
    const route: string = link.getAttribute("href")?.replace(/^#/, "") ?? "";
    link.classList.toggle("is-current", path.startsWith(route));
  }
}

function buildNav(): HTMLElement {
  const role: Role = requireUser().role;
  const nav: HTMLElement = createElement("nav", "topbar-nav");
  nav.setAttribute("aria-label", t("nav.menu"));
  for (const item of navItems.filter((candidate: NavItem): boolean => candidate.roles.includes(role))) {
    nav.append(createLink(item.route, t(item.labelKey)));
  }

  return nav;
}

function buildTools(): HTMLElement {
  const tools: HTMLDivElement = createElement("div", "topbar-tools");
  const readOnly: HTMLElement | null = requireUser().role === "Viewer"
    ? createBadge(t("app.readOnly"), "warning")
    : null;
  appendChildren(tools, readOnly, buildBell(), buildLanguageSwitch(), buildThemeSelect(), buildUserMenu());
  return tools;
}

/** "УКР | ENG" (SPEC §9.4). Switching re-renders the screen without reloading data (SPEC §10.1). */
function buildLanguageSwitch(): HTMLElement {
  const group: HTMLDivElement = createElement("div", "language-switch");
  group.setAttribute("role", "group");
  group.setAttribute("aria-label", t("nav.language"));
  group.append(buildLanguageButton("uk", "УКР"), createElement("span", "muted", " | "),
    buildLanguageButton("en", "ENG"));
  return group;
}

function buildLanguageButton(language: Language, label: string): HTMLButtonElement {
  const button: HTMLButtonElement = createButton(label, (): void => {
    setLanguage(language);
    renderTopBar(true);
    highlightCurrentNav();
    redrawCurrentPage();
  }, "button-link");
  button.setAttribute("aria-pressed", String(getLanguage() === language));
  button.lang = language;
  return button;
}

function buildThemeSelect(): HTMLElement {
  const options: { value: ThemeChoice; label: string }[] = [
    { value: "system", label: t("theme.system") },
    { value: "light", label: t("theme.light") },
    { value: "dark", label: t("theme.dark") },
  ];
  const select: HTMLSelectElement = createSelect(options, getTheme());
  select.setAttribute("aria-label", t("nav.theme"));
  select.addEventListener("change", (): void => {
    setTheme(select.value as ThemeChoice);
  });
  return select;
}

function buildUserMenu(): HTMLElement {
  const menu: HTMLDivElement = createElement("div", "user-menu");
  const panel: HTMLDivElement = createElement("div", "user-menu-panel");
  panel.hidden = true;
  const user: ReturnType<typeof requireUser> = requireUser();
  const toggle: HTMLButtonElement = createButton(user.displayName, (): void => {
    panel.hidden = panel.hidden === false;
  });
  // The role is left out on phones to keep the tools on one line.
  toggle.append(createElement("span", "hide-narrow", ` (${t(`roles.${user.role}`)})`));
  panel.append(createLink("/account/password", t("nav.changePassword")),
    createButton(t("nav.signOut"), signOutHandler));
  menu.append(toggle, panel);
  return menu;
}
